import { useCallback, useEffect, useRef, useState } from 'react';
import {
  complementosApi,
  type EntitlementProduto,
} from '@/features/complementos/api/complementosApi';

type Estado = {
  entitlement: EntitlementProduto | null;
  carregando: boolean;
  erro: string | null;
  /** Alguma verificação já terminou (com sucesso ou erro). */
  verificado: boolean;
};

/**
 * Direito de uso de um produto add-on (`'pdv'`, …). Mesmo desenho do
 * `usePosVendaEntitlement`: verifica ao montar e `recarregar(true)` força
 * uma consulta sem cache (ex.: logo depois de comprar).
 *
 * Diferenças deliberadas:
 * - Falha numa revalidação NÃO apaga o último estado conhecido. Um soluço
 *   de rede durante a checagem periódica não pode derrubar uma tela em
 *   uso (no PDV, isso jogaria fora o carrinho no meio da venda). Se o
 *   acesso foi mesmo revogado, as rotas do produto recusam e a tela cai no
 *   bloqueio pelo `avisoBloqueio`.
 * - Só a resposta da chamada mais recente vale: a verificação do mount e a
 *   forçada ao entrar na aba podem correr juntas e chegar fora de ordem.
 * - `verificandoPrimeiraVez` separa "ainda não sei" de "estou revalidando":
 *   só o primeiro merece tela de carregamento; nos demais a tela atual
 *   continua onde está.
 */
export function useProdutoEntitlement(codigo: string) {
  const [estado, setEstado] = useState<Estado>({
    entitlement: null,
    carregando: true,
    erro: null,
    verificado: false,
  });
  const ultimaChamada = useRef(0);

  const recarregar = useCallback(
    (forcar = false) => {
      const chamada = ++ultimaChamada.current;
      setEstado((s) => ({ ...s, carregando: true, erro: null }));
      return complementosApi.entitlement(codigo, forcar).then(
        (entitlement) => {
          if (chamada !== ultimaChamada.current) return;
          setEstado({ entitlement, carregando: false, erro: null, verificado: true });
        },
        (e: unknown) => {
          if (chamada !== ultimaChamada.current) return;
          const mensagem =
            e instanceof Error && e.message ? e.message : 'Falha ao verificar o acesso.';
          setEstado((s) => ({
            entitlement: s.entitlement,
            carregando: false,
            erro: mensagem,
            verificado: true,
          }));
        },
      );
    },
    [codigo],
  );

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const { entitlement, carregando, erro, verificado } = estado;
  return {
    entitlement,
    carregando,
    erro,
    ativo: entitlement?.ativo ?? false,
    verificandoPrimeiraVez: !verificado,
    recarregar,
  };
}

export type ProdutoEntitlement = ReturnType<typeof useProdutoEntitlement>;
